/** @jsxImportSource @emotion/react */

import React from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Form } from 'react-final-form'
import createDecorator from 'final-form-focus'
import Button from '@mui/material/Button'
import { SelectField } from 'components/Select'
import { InputField } from 'components/Input'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { FIND_EXTENSION_CONFIG, UPDATE_EXTENSION_CONFIG } from 'queries/extensionExams'
import { findExtensionConfig } from '__generatedTypes__/findExtensionConfig'
import { updateExtensionConfig, updateExtensionConfigVariables } from '__generatedTypes__/updateExtensionConfig'
import { ExtensionStatusEnum } from '__generatedTypes__/globalTypes'
import * as classes from './styles'

type StatusOption = {
  id: string
  title: string
  value: ExtensionStatusEnum
}

const STATUS_OPTIONS: StatusOption[] = [
  { id: 'default', title: 'Default', value: ExtensionStatusEnum.Okay },
  { id: 'info', title: 'Info', value: ExtensionStatusEnum.Info },
  { id: 'warning', title: 'Warning', value: ExtensionStatusEnum.Warning },
  { id: 'error', title: 'Error', value: ExtensionStatusEnum.Error },
]

type ConfigFormValues = {
  status: StatusOption
  message: string | null
}

const focusOnErrors = createDecorator<ConfigFormValues, Partial<ConfigFormValues>>()

const validate = (values: ConfigFormValues) =>
  values.status.value !== ExtensionStatusEnum.Okay && !values.message?.trim()
    ? { message: 'Message is required when status is not Default' }
    : {}

export const Config = () => {
  const { data, loading } = useQuery<findExtensionConfig>(FIND_EXTENSION_CONFIG, {
    fetchPolicy: 'network-only',
  })
  const [updateExtensionConfig] = useMutation<updateExtensionConfig, updateExtensionConfigVariables>(
    UPDATE_EXTENSION_CONFIG
  )

  const handleSubmit = async (values: ConfigFormValues) => {
    try {
      await updateExtensionConfig({
        variables: {
          params: {
            status: values.status.value,
            message: values.message,
          },
        },
      })
      toast.success('Extension configuration updated successfully')
    } catch (e) {
      toast.error('Extension configuration update failed')
    }
  }

  if (loading) {
    return <PageLoader />
  }

  const initialValues: ConfigFormValues = {
    status: STATUS_OPTIONS.find((option) => option.value === data?.findExtensionConfig.status) ?? STATUS_OPTIONS[0],
    message: data?.findExtensionConfig.message ?? '',
  }

  return (
    <div css={classes.container}>
      <Form
        decorators={[focusOnErrors]}
        validate={validate}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting }) => (
          <form css={classes.form} onSubmit={handleSubmit} data-testid="extension-config-form">
            <SelectField name="status" label="Status" items={STATUS_OPTIONS} fullWidth />

            <InputField
              name="message"
              label="Message"
              placeholder="e.g. We're experiencing issues with the extension. Our team is on it!"
              multiline
              fullWidth
            />

            <Button variant="contained" type="submit" color="blue" size="large" disabled={submitting} fullWidth>
              Save
            </Button>
          </form>
        )}
      />
    </div>
  )
}
